import React, { useEffect, useState } from "react";
import { usePageMeta } from "@/lib/usePageMeta";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { StarRating } from "@/components/StarRating";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function Profile() {
  usePageMeta({ noindex: true });
  const { user, refresh, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [bio, setBio] = useState("");
  const [myReviews, setMyReviews] = useState([]);

  useEffect(() => {
    if (!user) return;
    setName(user.name || ""); setBio(user.bio || "");
    api.get("/users/me/reviews").then(r => setMyReviews(r.data.items)).catch(()=>{});
  }, [user]);

  if (!user) {
    return (
      <div className="max-w-md mx-auto py-24 text-center">
        <h1 className="text-2xl mb-2">{t("profile_signin_title")}</h1>
        <p className="text-muted-foreground mb-6">{t("profile_signin_body")}</p>
        <Button onClick={() => nav("/login", { state: { from: "/profile" } })} data-testid="btn-login-profile">
          {t("nav_login")}
        </Button>
      </div>
    );
  }

  const save = async () => {
    try { await api.patch("/auth/profile", { name, bio }); await refresh(); toast.success(t("profile_saved")); }
    catch { toast.error(t("error_title")); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 space-y-10 animate-in fade-in duration-500">
      <div className="flex items-center gap-4">
        {user.picture ? <img src={user.picture} alt="" className="w-16 h-16 rounded-full" /> : <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center font-serif-en text-xl">{name?.[0]}</div>}
        <div>
          <h1 className="text-2xl font-serif-en">{user.name}</h1>
          <p className="text-sm text-muted-foreground">{user.email}</p>
        </div>
      </div>

      <div className="border border-border rounded-md p-6 bg-card space-y-4">
        <h2 className="text-xl">{t("profile_edit")}</h2>
        <div>
          <label htmlFor="profile-name" className="text-sm text-muted-foreground">{t("profile_name")}</label>
          <Input id="profile-name" value={name} onChange={e => setName(e.target.value)} data-testid="profile-name-input" />
        </div>
        <div>
          <label htmlFor="profile-bio" className="text-sm text-muted-foreground">{t("profile_bio")}</label>
          <Textarea id="profile-bio" value={bio} onChange={e => setBio(e.target.value)} rows={3} data-testid="profile-bio-input" />
        </div>
        <div className="flex gap-2">
          <Button onClick={save} data-testid="save-profile-btn">{t("profile_save")}</Button>
          <Button variant="outline" onClick={async () => { await logout(); nav("/"); }} data-testid="btn-logout-profile">{t("nav_logout")}</Button>
        </div>
      </div>

      <div>
        <h2 className="text-xl mb-4">{t("my_reviews")}</h2>
        {myReviews.length === 0 && <p className="text-muted-foreground">{t("profile_no_reviews")}</p>}
        <div className="space-y-3">
          {myReviews.map(r => (
            <div key={r.id} className="border border-border p-4 rounded-md bg-card" data-testid={`my-review-${r.id}`}>
              <div className="flex justify-between mb-2">
                <span className="text-xs text-muted-foreground uppercase tracking-wider">{r.target_type}</span>
                <StarRating value={r.rating} size={12} />
              </div>
              <p className="text-sm">{r.text || <em className="text-muted-foreground">{t("review_no_text")}</em>}</p>
              <div className="text-xs text-muted-foreground mt-2">{new Date(r.created_at).toLocaleDateString()}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
